import * as React from "react";
import "./WorldContainer.component.css";
import { useEffect, useRef, useState } from "react";
import { renderer } from "../../../modules/rendering/RendererGlobals";
import { mouseService } from "../../../modules/inputs/devices/MouseService";
import { camera } from "../../../modules/rendering/camera";
import { renderManager } from "../../../modules/xr/RenderManager";
import { mainScene } from "../../../modules/rendering/mainScene";
import { EnableInputDevicesMessage, ToolViewerViewportChangedMessage } from "flavr-common";
import { busService } from "../../../modules/bus";
import { inputService } from "../../../modules/inputs/input/InputService";
import { MessageService } from "../../services/message.service";
import { Mesh, MeshBasicMaterial, MeshPhongMaterial, MeshStandardMaterial, ShaderMaterial } from "three";
import { useModelUploadStateStore } from "../../store/model-upload-state.store";
import { useTranslation } from "react-i18next";
const busConnection = busService.connection("WorldContainer");
export function WorldContainer({ children, vrMode, allowFileDrop, onFileDropped }) {
    const [isDraggingOver, setIsDraggingOver] = useState(false);
    const [isWireframe, setIsWireframe] = useState(false);
    const observer = useRef();
    const worldContainer = useRef(null);
    const modelUploadInProgress = useModelUploadStateStore((state) => state.inProgress);
    const { t } = useTranslation();
    useEffect(() => {
        if (!worldContainer.current) {
            throw new Error("worldContainer is undefined");
        }
        worldContainer.current.appendChild(renderer.domElement);
        mouseService.register(renderer.domElement);
        inputService.registerListeners();
        inputService.deleteAction("ToggleWireframe");
        inputService
            .registerAction("ToggleWireframe", () => {
            setIsWireframe((prev) => !prev);
        }, t("common:key-description-key-z", "Toggle wireframe mode"))
            .addKey("KeyZ")
            .asDev();
        inputService.deleteAction("ToggleShadow");
        inputService
            .registerAction("ToggleShadow", () => {
            renderer.shadowMap.enabled = !renderer.shadowMap.enabled;
            console.info("Shadows are " + (renderer.shadowMap.enabled ? "ON" : "OFF"));
        }, t("common:key-description-key-s", "Toggle shadows"))
            .addKey("KeyS")
            .asDev();
        observer.current = new ResizeObserver(determineAspectRatio);
        if (!vrMode) {
            const startObserving = () => {
                var _a;
                // In rare cases, the observer is undefined here
                try {
                    if (worldContainer.current) {
                        (_a = observer.current) === null || _a === void 0 ? void 0 : _a.observe(worldContainer.current);
                    }
                    else {
                        throw new Error("worldContainer is undefined");
                    }
                }
                catch (e) {
                    console.warn("Could not observe world container, trying again in 100ms", e);
                    setTimeout(startObserving, 100);
                }
            };
            startObserving();
        }
        busConnection.send(new EnableInputDevicesMessage(true));
        initializeDropTarget();
    }, []);
    useEffect(() => {
        mainScene.traverse((node) => {
            if (node instanceof Mesh) {
                if (node.material instanceof MeshBasicMaterial ||
                    node.material instanceof MeshPhongMaterial ||
                    node.material instanceof ShaderMaterial ||
                    node.material instanceof MeshStandardMaterial) {
                    node.material.wireframe = isWireframe;
                }
            }
        });
    }, [isWireframe]);
    useEffect(() => {
        if (!observer.current || !worldContainer.current) {
            throw new Error("observer or worldContainer is undefined");
        }
        observer.current.observe(worldContainer.current);
        window.addEventListener("resize", determineAspectRatio);
        determineAspectRatio();
        return () => {
            var _a;
            (_a = observer.current) === null || _a === void 0 ? void 0 : _a.disconnect();
            window.removeEventListener("resize", determineAspectRatio);
        };
    }, [vrMode]);
    const determineAspectRatio = () => {
        const worldContainer = document.getElementById("world-container");
        if (!worldContainer) {
            throw new Error("worldContainer is undefined");
        }
        const width = worldContainer.offsetWidth;
        const height = worldContainer.offsetHeight;
        if (width <= 0 || height <= 0)
            return;
        renderManager.updateRenderSize(width, height);
        if (!renderManager.isXRPresenting()) {
            camera.perspectiveCam.aspect = width / height;
            camera.perspectiveCam.updateProjectionMatrix();
            renderer.render(mainScene, camera.perspectiveCam);
            busConnection.send(new ToolViewerViewportChangedMessage(width / height));
        }
    };
    const initializeDropTarget = () => {
        if (!worldContainer.current) {
            throw new Error("worldContainer is undefined");
        }
        const setStateAndStopEvent = (state, event) => {
            setIsDraggingOver(state);
            event.preventDefault();
            event.stopPropagation();
        };
        if (allowFileDrop) {
            worldContainer.current.addEventListener("dragover", (event) => {
                var _a;
                const items = Array.from(((_a = event.dataTransfer) === null || _a === void 0 ? void 0 : _a.items) || []);
                if (items.length === 1 && items[0].kind === "file") {
                    setStateAndStopEvent(true, event);
                }
                else {
                    setStateAndStopEvent(false, event);
                }
            });
            worldContainer.current.addEventListener("dragleave", (event) => {
                setStateAndStopEvent(false, event);
            });
            worldContainer.current.addEventListener("dragend", (event) => {
                setStateAndStopEvent(false, event);
            });
            worldContainer.current.addEventListener("drop", (event) => {
                var _a;
                setStateAndStopEvent(false, event);
                if (useModelUploadStateStore.getState().inProgress) {
                    MessageService.showMessage("info", t("common:info-on-model-drop-please-wait", "Please wait until the current model upload is finished"));
                    return;
                }
                const files = ((_a = event.dataTransfer) === null || _a === void 0 ? void 0 : _a.files) || [];
                if (files.length > 1) {
                    MessageService.showMessage("error", t("common:error-on-model-drop-only-one", "You can only upload one file at a time"));
                }
                else if (files.length === 1) {
                    if (onFileDropped) {
                        onFileDropped(files[0]);
                    }
                    else {
                        console.error("onFileDropped is undefined");
                    }
                }
            });
        }
    };
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { id: "world-container", ref: worldContainer }),
        React.createElement("div", { className: `world-container-overlay ${isDraggingOver && !modelUploadInProgress ? "world-container-drop-target" : ""}` }, children)));
}
